/**
 * Jack & Jones — cart drawer layer (Horizon's theme-drawer#cart-drawer, snippets/cart-drawer.liquid).
 * Horizon keeps the cart logic (lines, quantities, removal, totals, checkout); this adds:
 *
 * - Section anchors while the drawer is open: desktop `header` + `cuerpo`, mobile `carrito` (the
 *   names of the Figma frames 614:22644 / 73:1232). Only while open, so a closed drawer never marks
 *   anything on the pages behind it.
 * - The footer always fixed at the bottom: Horizon's [cart-summary-sticky] kept at "true".
 * - «Para regalo» (snippets/jj-cart-line-gift.liquid): line item property through /cart/change.js.
 * - «Deseo facturar como empresa» (snippets/jj-cart-drawer-summary.liquid): ticking it opens the CUIT
 *   and company fields; GUARDAR checks the CUIT and saves them as cart attributes through
 *   /cart/update.js, unticking removes them.
 * - «COMPLETA TU LOOK» (snippets/jj-cart-recommendations.liquid): fetches the recommendations
 *   section and adds products with /cart/add.js, saying on the button what the API answered (rule A3).
 *
 * Every cart change resolves a CartLinesUpdateEvent with the new cart, so Horizon re-renders the
 * drawer and updates the header count as with its own controls.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const mobileQuery = window.matchMedia('(max-width: 749px)');
const routes = /** @type {any} */ (window).Theme?.routes || {};

/** @returns {HTMLElement | null} */
const getDrawer = () => document.querySelector('theme-drawer#cart-drawer');

/* ----------------------------------------------------------------------------------------------
 * Section anchors (data-section)
 * ------------------------------------------------------------------------------------------- */

function markSections() {
  const drawer = getDrawer();
  if (!drawer) return;
  const dialog = drawer.querySelector('dialog');
  const isOpen = Boolean(dialog?.open);

  drawer.querySelectorAll('[data-section]').forEach((element) => element.removeAttribute('data-section'));
  if (!isOpen) return;

  if (mobileQuery.matches) {
    drawer.querySelector('.cart-drawer__inner')?.setAttribute('data-section', 'carrito');
    return;
  }
  drawer.querySelector('.jj-cart-drawer__header')?.setAttribute('data-section', 'header');
  drawer.querySelector('.cart-drawer__content')?.setAttribute('data-section', 'cuerpo');
}

/* ----------------------------------------------------------------------------------------------
 * Fixed footer (Figma «Pie», 614:22645: always at the bottom, the lines and «COMPLETA TU LOOK»
 * scroll above it)
 * ------------------------------------------------------------------------------------------- */

/**
 * Horizon (assets/cart-drawer.js) sets [cart-summary-sticky] to 'false' when the footer takes more than
 * half the drawer: it left 'false' for good after adding from the product page with an empty cart
 * (QA h7636-carrito-15) and, on desktop, with «Descuento» open or after an invalid code (prueba
 * funcional J&J), with TOTAL and «FINALIZAR COMPRA» under the fold. In Jack & Jones the footer is
 * always fixed and assets/jj-cart-drawer.css caps it at 75% of the content (the accordions scroll
 * inside it past that), so the attribute is put back to 'true' whenever Horizon changes it.
 */
function updateSummarySticky() {
  const dialog = getDrawer()?.querySelector('dialog');
  if (!dialog) return;
  if (dialog.getAttribute('cart-summary-sticky') !== 'true') dialog.setAttribute('cart-summary-sticky', 'true');
}

/* ----------------------------------------------------------------------------------------------
 * Cart requests
 * ------------------------------------------------------------------------------------------- */

/**
 * Posts to a /cart endpoint inside a CartLinesUpdateEvent, so Horizon refreshes the drawer and the
 * header count with the cart it gets back.
 * @param {Element} source - element the event is dispatched from (inside the drawer)
 * @param {string} url
 * @param {object} body
 * @param {{ action: string, lines: object[] }} event
 * @returns {Promise<{ ok: boolean, data: any }>}
 */
async function cartRequest(source, url, body, event) {
  /** @type {any} */
  let deferred = null;
  try {
    deferred = CartLinesUpdateEvent.createPromise();
    source.dispatchEvent(
      new CartLinesUpdateEvent({ ...event, context: 'cart', promise: deferred.promise })
    );
  } catch (error) {
    deferred = null;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    // /cart/change.js answers with the cart; /cart/add.js only with the added items
    const cart = response.ok && typeof data.item_count === 'number' ? data : await fetchCart();
    deferred?.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, source: 'jj-cart-drawer', didError: !response.ok },
    });
    return { ok: response.ok, data };
  } catch (error) {
    deferred?.reject(error);
    throw error;
  }
}

async function fetchCart() {
  const response = await fetch(`${routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/* ----------------------------------------------------------------------------------------------
 * «Para regalo»
 * ------------------------------------------------------------------------------------------- */

/** @param {HTMLInputElement} input */
async function toggleGift(input) {
  const line = Number(input.dataset.line);
  const property = input.dataset.property || '';
  /** @type {Record<string, string>} */
  let properties = {};
  try {
    properties = JSON.parse(input.dataset.properties || '{}') || {};
  } catch (error) {
    properties = {};
  }
  if (input.checked) properties[property] = input.dataset.value || '';
  else delete properties[property];

  input.setAttribute('aria-busy', 'true');
  input.disabled = true;
  try {
    const { ok, data } = await cartRequest(
      input,
      `${routes.cart_change_url || '/cart/change'}.js`,
      { line, quantity: Number(input.dataset.quantity) || 1, properties },
      { action: 'update', lines: [{ id: input.dataset.key || String(line), quantity: Number(input.dataset.quantity) || 1 }] }
    );
    if (!ok) showLineError(input, line, data.description || data.message || '');
  } catch (error) {
    showLineError(input, line, '');
  } finally {
    if (input.isConnected) {
      input.disabled = false;
      input.removeAttribute('aria-busy');
    }
  }
}

/**
 * The change didn't go through: the checkbox goes back and Horizon's error line of that cart line
 * says why, right there (rule A3).
 * @param {HTMLInputElement} input
 * @param {number} line
 * @param {string} message
 */
function showLineError(input, line, message) {
  input.checked = !input.checked;
  const row = input.closest('.cart-items__table-row');
  const container = row?.querySelector(`[ref="cartItemErrorContainer-${line}"]`);
  const text = row?.querySelector(`[ref="cartItemError-${line}"]`);
  if (!container || !text) return;
  text.textContent = message || input.dataset.error || '';
  container.classList.remove('hidden');
}

/* ----------------------------------------------------------------------------------------------
 * «Deseo facturar como empresa»
 * ------------------------------------------------------------------------------------------- */

const CUIT_WEIGHTS = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

/**
 * Argentine CUIT: 11 digits, typed with or without hyphens, spaces or dots. The last one is the check
 * digit: 11 − (Σ first ten × CUIT_WEIGHTS mod 11), where 11 gives 0 and 10 gives no valid CUIT.
 * @param {string} value
 * @returns {{ cuit: string, error: '' } | { cuit: '', error: 'length' | 'check' }}
 */
function parseCuit(value) {
  const digits = value.replace(/[\s.-]/g, '');
  if (!/^\d{11}$/.test(digits)) return { cuit: '', error: 'length' };

  const sum = CUIT_WEIGHTS.reduce((total, weight, index) => total + weight * Number(digits[index]), 0);
  const check = (11 - (sum % 11)) % 11;
  if (check === 10 || check !== Number(digits[10])) return { cuit: '', error: 'check' };

  return { cuit: `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits[10]}`, error: '' };
}

/**
 * The pieces of the invoice block an element belongs to.
 * @param {Element | null | undefined} element
 */
function getInvoice(element) {
  const block = element?.closest('[data-jj-cart-invoice-block]');
  if (!(block instanceof HTMLElement)) return null;
  const checkbox = block.querySelector('[data-jj-cart-invoice]');
  const fields = block.querySelector('[data-jj-cart-invoice-fields]');
  const cuit = block.querySelector('[data-jj-cart-invoice-cuit]');
  const company = block.querySelector('[data-jj-cart-invoice-company]');
  const save = block.querySelector('[data-jj-cart-invoice-save]');
  const error = block.querySelector('[data-jj-cart-invoice-error]');
  const status = block.querySelector('[data-jj-cart-invoice-status]');
  if (
    !(checkbox instanceof HTMLInputElement) ||
    !(fields instanceof HTMLElement) ||
    !(cuit instanceof HTMLInputElement) ||
    !(company instanceof HTMLInputElement) ||
    !(save instanceof HTMLButtonElement) ||
    !error ||
    !status
  ) {
    return null;
  }
  return { block, checkbox, fields, cuit, company, save, error, status };
}

/** @typedef {NonNullable<ReturnType<typeof getInvoice>>} Invoice */

/**
 * What the shopper has in the block, so the drawer re-render Horizon does after every cart change
 * neither unticks it nor loses what was typed before GUARDAR.
 * @type {{ checked: boolean, cuit: string, company: string } | null}
 */
let invoiceDraft = null;

/** @param {Invoice} invoice */
function rememberInvoice(invoice) {
  invoiceDraft = { checked: invoice.checkbox.checked, cuit: invoice.cuit.value, company: invoice.company.value };
}

/** After a re-render, puts back what the shopper had (rendered from the cart attributes otherwise). */
function restoreInvoice() {
  const invoice = getInvoice(getDrawer()?.querySelector('[data-jj-cart-invoice-block]'));
  if (!invoice) return;
  if (invoiceDraft) {
    if (invoice.checkbox.checked !== invoiceDraft.checked) invoice.checkbox.checked = invoiceDraft.checked;
    if (invoice.cuit.value !== invoiceDraft.cuit) invoice.cuit.value = invoiceDraft.cuit;
    if (invoice.company.value !== invoiceDraft.company) invoice.company.value = invoiceDraft.company;
  }
  invoice.fields.hidden = !invoice.checkbox.checked;
}

/**
 * Error under the CUIT row (an invalid CUIT marks the field, a failed request doesn't) or the
 * «guardado» line; each one clears the other.
 * @param {Invoice} invoice
 * @param {{ error?: string, invalid?: boolean, status?: string }} message
 */
function setInvoiceMessage(invoice, { error = '', invalid = false, status = '' }) {
  invoice.error.textContent = error;
  invoice.status.textContent = status;
  if (invalid) invoice.cuit.setAttribute('aria-invalid', 'true');
  else invoice.cuit.removeAttribute('aria-invalid');
}

/**
 * Sets cart attributes; an empty value removes that attribute from the cart.
 * @param {Record<string, string>} attributes
 */
async function updateCartAttributes(attributes) {
  const response = await fetch(`${routes.cart_update_url || '/cart/update'}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes }),
  });
  if (!response.ok) throw new Error(String(response.status));
  return response.json();
}

/**
 * Ticked: opens the fields (GUARDAR is what saves). Unticked: closes them and removes the three
 * attributes; if that fails the box goes back to ticked and says so.
 * @param {HTMLInputElement} input
 */
async function toggleInvoice(input) {
  const invoice = getInvoice(input);
  if (!invoice) return;
  const { dataset } = invoice.block;
  setInvoiceMessage(invoice, {});
  invoice.fields.hidden = !input.checked;
  rememberInvoice(invoice);
  if (input.checked) return;

  input.setAttribute('aria-busy', 'true');
  try {
    await updateCartAttributes({
      [dataset.attribute || '']: '',
      [dataset.cuitAttribute || '']: '',
      [dataset.companyAttribute || '']: '',
    });
  } catch (error) {
    input.checked = true;
    invoice.fields.hidden = false;
    rememberInvoice(invoice);
    setInvoiceMessage(invoice, { error: dataset.error || '' });
  } finally {
    input.removeAttribute('aria-busy');
  }
}

/**
 * GUARDAR (or Enter in a field): an invalid CUIT is only reported; a valid one is saved with
 * hyphens, together with «Facturar como empresa» and the company name when there is one.
 * @param {Invoice} invoice
 */
async function saveInvoice(invoice) {
  const { block, save, cuit, company } = invoice;
  const { dataset } = block;
  if (block.dataset.jjBusy) return;

  const parsed = parseCuit(cuit.value);
  if (parsed.error) {
    setInvoiceMessage(invoice, {
      error: (parsed.error === 'length' ? dataset.errorLength : dataset.errorCheck) || '',
      invalid: true,
    });
    return;
  }

  cuit.value = parsed.cuit;
  company.value = company.value.trim();
  rememberInvoice(invoice);
  setInvoiceMessage(invoice, {});

  block.dataset.jjBusy = 'true';
  save.setAttribute('aria-busy', 'true');
  try {
    await updateCartAttributes({
      [dataset.attribute || '']: dataset.value || '',
      [dataset.cuitAttribute || '']: parsed.cuit,
      [dataset.companyAttribute || '']: company.value,
    });
    setInvoiceMessage(invoice, { status: dataset.saved || '' });
  } catch (error) {
    setInvoiceMessage(invoice, { error: dataset.error || '' });
  } finally {
    delete block.dataset.jjBusy;
    save.removeAttribute('aria-busy');
  }
}

/* ----------------------------------------------------------------------------------------------
 * «COMPLETA TU LOOK»
 * ------------------------------------------------------------------------------------------- */

/** @type {WeakMap<HTMLElement, string>} */
const loadedFor = new WeakMap();

/**
 * What was put in each slot for its key, to put it back if a re-render empties the slot.
 * @type {WeakMap<HTMLElement, { key: string, node: Element | null }>}
 */
const shownIn = new WeakMap();

/**
 * Loads the suggestions when the slot is new or the cart products changed since the last load.
 * Horizon morphs the drawer after every cart change (+/−, «Eliminar», «Facturar como empresa») and
 * the server renders the slot empty: the slot carries data-skip-subtree-update so the morph leaves
 * its cards alone, and if anything still empties it with the same key, the same cards go back in
 * (same nodes, so a size already picked stays) instead of «COMPLETA TU LOOK» vanishing until the
 * cart products change (QA h7636-carrito-41/43).
 */
function refreshRecommendations() {
  const drawer = getDrawer();
  const reco = drawer?.querySelector('[data-jj-cart-reco]');
  if (!(reco instanceof HTMLElement)) return;
  const slot = reco.querySelector('[data-jj-cart-reco-slot]');
  if (!(slot instanceof HTMLElement)) return;

  const key = `${reco.dataset.url}|${reco.dataset.cartProducts}`;
  if (loadedFor.get(slot) === key) {
    const shown = shownIn.get(slot);
    if (shown?.key === key && shown.node && !slot.contains(shown.node)) slot.replaceChildren(shown.node);
    return;
  }
  loadedFor.set(slot, key);

  fetch(reco.dataset.url || '', { headers: { Accept: 'text/html' } })
    .then((response) => (response.ok ? response.text() : ''))
    .then((html) => {
      if (loadedFor.get(slot) !== key) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('.jj-cart-reco__inner');
      const node = inner && inner.children.length ? document.importNode(inner, true) : null;
      shownIn.set(slot, { key, node });
      slot.replaceChildren(...(node ? [node] : []));
    })
    .catch(() => {
      shownIn.delete(slot);
      slot.replaceChildren();
    });
}

/**
 * @param {HTMLFormElement} form
 * @param {string} text
 * @param {number} [resetAfter]
 */
function setRecoLabel(form, text, resetAfter) {
  const label = form.querySelector('[data-jj-cart-reco-label]');
  if (!label) return;
  label.textContent = text;
  window.clearTimeout(Number(form.dataset.jjTimer));
  if (resetAfter) {
    form.dataset.jjTimer = String(
      window.setTimeout(() => {
        label.textContent = form.dataset.labelAdd || '';
      }, resetAfter)
    );
  }
}

/** @param {HTMLFormElement} form */
async function addRecommendation(form) {
  if (form.dataset.jjBusy) return;
  const button = form.querySelector('[data-jj-cart-reco-add]');
  const select = form.querySelector('select[name="id"]');
  const message = form.closest('.jj-cart-reco-card')?.querySelector('[data-jj-cart-reco-message]');
  const variantId = select instanceof HTMLSelectElement ? select.value : '';
  if (!variantId || !(button instanceof HTMLButtonElement)) return;

  form.dataset.jjBusy = 'true';
  button.setAttribute('aria-busy', 'true');
  if (message) message.textContent = '';
  setRecoLabel(form, form.dataset.labelAdding || '');

  try {
    const { ok, data } = await cartRequest(
      form,
      routes.cart_add_url || '/cart/add.js',
      { items: [{ id: Number(variantId), quantity: 1 }] },
      { action: 'add', lines: [{ merchandiseId: variantId, quantity: 1 }] }
    );
    if (ok) {
      setRecoLabel(form, form.dataset.labelAdded || '', 2500);
    } else {
      // e.g. «El producto está agotado»: said on the card that was touched
      setRecoLabel(form, form.dataset.labelAdd || '');
      if (message) message.textContent = data.description || data.message || form.dataset.labelError || '';
    }
  } catch (error) {
    setRecoLabel(form, form.dataset.labelAdd || '');
    if (message) message.textContent = form.dataset.labelError || '';
  } finally {
    delete form.dataset.jjBusy;
    button.removeAttribute('aria-busy');
  }
}

/* ----------------------------------------------------------------------------------------------
 * Wiring
 * ------------------------------------------------------------------------------------------- */

/** @param {Event} event */
function onChange(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.matches('[data-jj-cart-gift]')) toggleGift(target);
  else if (target.matches('[data-jj-cart-invoice]')) toggleInvoice(target);
}

/** @param {SubmitEvent} event */
function onSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-jj-cart-reco-form]')) return;
  event.preventDefault();
  addRecommendation(form);
}

/** @param {MouseEvent} event */
function onClick(event) {
  const save = event.target instanceof Element ? event.target.closest('[data-jj-cart-invoice-save]') : null;
  const invoice = getInvoice(save);
  if (invoice) saveInvoice(invoice);
}

/** Typing in the invoice fields: what was said about the last value goes away. @param {Event} event */
function onInput(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.matches('[data-jj-cart-invoice-cuit], [data-jj-cart-invoice-company]')) return;
  const invoice = getInvoice(target);
  if (!invoice) return;
  rememberInvoice(invoice);
  if (target === invoice.cuit) setInvoiceMessage(invoice, {});
  else invoice.status.textContent = '';
}

/** Enter in the invoice fields saves them, like GUARDAR. @param {KeyboardEvent} event */
function onKeydown(event) {
  const target = event.target;
  if (event.key !== 'Enter' || event.isComposing || !(target instanceof HTMLInputElement)) return;
  if (!target.matches('[data-jj-cart-invoice-cuit], [data-jj-cart-invoice-company]')) return;
  const invoice = getInvoice(target);
  if (!invoice) return;
  event.preventDefault();
  saveInvoice(invoice);
}

function init() {
  const drawer = getDrawer();
  if (!drawer) return;

  drawer.addEventListener('change', onChange);
  drawer.addEventListener('submit', onSubmit);
  drawer.addEventListener('click', onClick);
  drawer.addEventListener('input', onInput);
  drawer.addEventListener('keydown', onKeydown);

  // Horizon re-renders the drawer contents after every cart change, toggles [open] on the dialog
  // (and on the «Instrucciones especiales» / «Descuento» accordions) and [cart-summary-sticky]
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      markSections();
      refreshRecommendations();
      restoreInvoice();
      updateSummarySticky();
    });
  }).observe(drawer, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['open', 'class', 'cart-summary-sticky'],
  });

  mobileQuery.addEventListener('change', markSections);
  markSections();
  refreshRecommendations();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
